import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// MOCK DATA for analytics
const MOCK_CAMPAIGN_PERFORMANCE = [
  { name: 'Campaign A', impressions: 4000, clicks: 240, ctr: 6.0 },
  { name: 'Campaign B', impressions: 3000, clicks: 198, ctr: 6.6 },
  { name: 'Campaign C', impressions: 2000, clicks: 98, ctr: 4.9 },
  { name: 'Campaign D', impressions: 2780, clicks: 108, ctr: 3.9 },
  { name: 'Campaign E', impressions: 1890, clicks: 48, ctr: 2.5 },
];

const MOCK_DAILY_PERFORMANCE = [
  { date: '2023-07-01', impressions: 1200, clicks: 78 },
  { date: '2023-07-02', impressions: 1300, clicks: 82 },
  { date: '2023-07-03', impressions: 1400, clicks: 86 },
  { date: '2023-07-04', impressions: 1350, clicks: 80 },
  { date: '2023-07-05', impressions: 1500, clicks: 92 },
  { date: '2023-07-06', impressions: 1600, clicks: 100 },
  { date: '2023-07-07', impressions: 1750, clicks: 120 },
];

const MOCK_PLATFORM_DISTRIBUTION = [
  { name: 'Facebook', value: 35 },
  { name: 'Instagram', value: 25 },
  { name: 'Twitter', value: 15 },
  { name: 'Google', value: 20 },
  { name: 'LinkedIn', value: 5 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return '#4CAF50';
    case 'paused':
      return '#FFA726';
    case 'completed':
      return '#9E9E9E';
    default:
      return '#9E9E9E';
  }
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Technology':
      return '#2196F3';
    case 'Travel':
      return '#FF9800';
    case 'Finance':
      return '#4CAF50';
    case 'Health':
      return '#E91E63';
    case 'Education':
      return '#9C27B0';
    default:
      return '#9E9E9E';
  }
};

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [loading, setLoading] = useState(false);
  
  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value);
  };

  const navigateToAdStrategist = () => {
    navigate('/ad-strategist');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Analytics Dashboard</Typography>
        <Button variant="contained" onClick={navigateToAdStrategist}>
          Create New Campaign
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Performance Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Impressions
              </Typography>
              <Typography variant="h4">
                14,320
              </Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +12.5% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Clicks
              </Typography>
              <Typography variant="h4">
                856
              </Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +8.2% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Average CTR
              </Typography>
              <Typography variant="h4">
                5.98%
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                -0.5% from last period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Active Campaigns
              </Typography>
              <Typography variant="h4">
                5
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                2 new this period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Campaign Performance */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Campaign Performance
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={MOCK_CAMPAIGN_PERFORMANCE}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="impressions" fill="#8884d8" name="Impressions" />
                <Bar yAxisId="right" dataKey="clicks" fill="#82ca9d" name="Clicks" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Platform Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Platform Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={MOCK_PLATFORM_DISTRIBUTION}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={130}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {MOCK_PLATFORM_DISTRIBUTION.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Daily Performance */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Daily Performance
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={MOCK_DAILY_PERFORMANCE}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="impressions"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
                <Line yAxisId="right" type="monotone" dataKey="clicks" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Campaign Details Table */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MOCK_CAMPAIGN_PERFORMANCE.map((campaign) => (
                    <TableRow
                      key={campaign.name}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {campaign.name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Active"
                          size="small"
                          sx={{ backgroundColor: getStatusColor('active'), color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>
                        {campaign.name === 'Campaign A' && 'Facebook, Instagram'}
                        {campaign.name === 'Campaign B' && 'Google, Twitter'}
                        {campaign.name === 'Campaign C' && 'Facebook, LinkedIn'}
                        {campaign.name === 'Campaign D' && 'Instagram, Twitter'}
                        {campaign.name === 'Campaign E' && 'Google, Facebook'}
                      </TableCell>
                      <TableCell align="right">{campaign.impressions.toLocaleString()}</TableCell>
                      <TableCell align="right">{campaign.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{campaign.ctr.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPage; 